import { SuspenseButton } from "@common/components/utility";
import SignaturePad from "@common/components/wrappers/SignaturePad";
import { SalesPackageModel, VisitPublicModel } from "@common/models";
import { formatCurrency, prettyPrintAge } from "@common/utils";
import { normalizePhone } from "@common/utils/phoneUtils";
import { calculateEstimateSubtotals, formatEstimateItemAmount, formatEstimateItemQuantity } from "@common/utils/pricingUtils";
import classnames from "classnames";
import { DateTime } from "luxon";
import { observer } from "mobx-react";
import React from "react";
import { Table } from "react-bootstrap";

interface ITreatmentPlanConsentProps {
  visit: VisitPublicModel
  onSave: (signatureDataUrl: string) => void,
}

export const TreatmentPlanConsent = observer(({ visit, onSave }: ITreatmentPlanConsentProps) => {
  const { subtotalMin, subtotalMax } = calculateEstimateSubtotals(visit.salesPackagePrice, visit.visitSalesItems.filter(i => !i.isDiscountItem));
  const sigRef = React.useRef<SignaturePad>(null);
  const [isSigned, setIsSigned] = React.useState(false);

  const onSaveSignature = () => {
    if (sigRef.current !== null)
      onSave(sigRef.current.toDataURL());
  };

  const onSignatureChanged = () =>
    setIsSigned(sigRef.current !== null && !sigRef.current.isEmpty());

  const { packageName, packagePrice, items } = SalesPackageModel.OrderPublicSalesItems(visit.visitSalesItems);

    return <div className="d-flex flex-column gap-3">
        <h1 style={{ fontSize: "1.75rem" }}>Treatment Plan and Consent to Services</h1>
        <div className="d-flex flex-column gap-1">
            <div>Vetco Total Care {visit.hospitalName}</div>
            <div>{visit.storeAddress}</div>
            <div>{visit.storeCity}, {visit.storeStateAbbreviation} {visit.storeZipCode}</div>
            <div>{visit.hospitalPhone ? normalizePhone(visit.hospitalPhone) : visit.storePhone}</div>
        </div>
        <div className="d-flex flex-column gap-1">
            <div><span className="fw-bold">Date:</span> {visit.visitDate?.toLocaleString(DateTime.DATE_FULL)}</div>
            <div><span className="fw-bold">Client name:</span> {visit.clientFirstName} {visit.clientLastName}</div>
            <div><span className="fw-bold">Client address:</span> {visit.clientAddress}{visit.clientAddress2 && `, ${visit.clientAddress2}`}, {visit.clientCity}, {visit.clientStateAbbreviation} {visit.clientZipCode}</div>
            <div><span className="fw-bold">Patient name:</span> {visit.petName}</div>
            <div><span className="fw-bold">Species:</span> {visit.petSpeciesName}</div>
            <div><span className="fw-bold">Breed:</span> {visit.petBreedName}</div>
            <div><span className="fw-bold">Age:</span> {prettyPrintAge(visit.petBirthDate)}</div>
            <div><span className="fw-bold">Weight:</span> {`${visit.petWeight} lbs`}</div>
        </div>
        <div className="d-flex flex-column gap-1">
            <div className="f-4"><span className="fw-bold">Plan</span></div>
            <div className="mt-2">
                <Table className="mb-0" hover striped size="sm">
                    <thead>
                        <tr>
                            <th>Description</th>
                            <th className="text-end" style={{ width: "5rem" }}>Qty</th>
                            <th className="text-end" style={{ width: "5rem" }}>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {packageName && packagePrice &&
                            <tr key={`salesPackage-row`} className={classnames("fw-bold", "gray-200")}>
                                <td>{packageName} Package</td>
                                <td></td>
                                <td className="text-end">{formatCurrency(packagePrice)}</td>
                            </tr>
                        }
                        {items.filter(i => !i.isDiscountItem && !i.wasDeclinedByVeterinarian).map((item, idx) =>
                            <tr key={`tp-item-${idx}`}>
                                <td style={{ paddingLeft: (item.salesPackageName === undefined) ? "0.5rem" : "2rem" }}>
                                    {item.wasDeclinedByClient && <span style={{ color: "$red" }}>Declined:</span>} {item.salesItemName} {item.salesItemOptionDisplayName}
                                </td>
                                <td className="text-end">{formatEstimateItemQuantity(item)}</td>
                                <td className="text-end">{formatEstimateItemAmount(item)}</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>
        </div>
        <div className="d-flex flex-row justify-content-between gap-3" >
            {subtotalMin === subtotalMax &&
                <>
                    <div>Subtotal (excl. taxes &amp; discounts)</div>
                    <div style={{ float: "right" }}><span className="fw-bold">{formatCurrency(subtotalMin)}</span></div>
                </>
            }
            {subtotalMin !== subtotalMax &&
                <>
                    <div>Subtotal Range (excl. taxes &amp; discounts)</div>
                    <div className="text-end"><span className="fw-bold">{formatCurrency(subtotalMin)} - {formatCurrency(subtotalMax)}</span></div>
                </>
            }
        </div>
        {items.some(i => i.isDiscountItem) &&
            <div className="d-flex flex-column gap-1" >
                <div><strong>Coupons and/or Discounts included in your visit today</strong></div>
                {items.filter(i => i.isDiscountItem).map((i, idx) =>
                    <div key={`item-discount-${idx}`}>{i.salesItemName}{!_.isEmpty(i.salesItemOptionDisplayName) && `: ${i.salesItemOptionDisplayName.replace("_", "")}`}</div>
                )}
            </div>
        }
        {visit.hasHospitalPrescriptions() && <div className="d-flex flex-column gap-1">
            <div className="f-4"><span className="fw-bold">Prescriptions Added to File</span></div>
            <div className="mt-2">
                <Table className="mb-0" hover striped size="sm">
                    <thead>
                        <tr>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                    {visit.hospitalPrescriptions.map((p) =>
                        <tr key={p.hospitalPrescriptionId}>
                            <td className="ps-2">{p.fullName} {p.optionDisplayName}</td>
                        </tr>)}
                    </tbody>
                </Table>
            </div>
        </div>}
        <div className="d-flex flex-column gap-3" style={{ fontSize: "smaller" }}>
            <div>By signing this document I certify I have read this document, understand it, and have had all my questions answered to my satisfaction and I agree to the conditions of treatment.</div>
            <div>I understand that this estimate of the fees for veterinary services is provided to me and that I am encouraged to discuss all fees related to such care before services are rendered during my pet’s ongoing medical treatment. I acknowledge that payment is due at time services are rendered.</div>
            <div>I, the below signed owner, agent of the owner, or Good Samaritan responsible for seeking veterinary care the pet identified, certify that I am eighteen years of age or over.</div>
        </div>
        <div className="d-flex flex-column gap-1">
            <div className="f-4"><span className="fw-bold">Signature of Owner/Agent</span></div>
            <div style={{ border: "1px solid gray" }}>
                <SignaturePad height={150} ref={sigRef} onEndStroke={onSignatureChanged} />
            </div>
            <div>
                Date of signing: {DateTime.now().toLocaleString(DateTime.DATE_FULL)}
            </div>
        </div>
        <div>
            <SuspenseButton style={{ width: "100%" }} tag={''} variant="primary" disabled={!isSigned} aria-label="save consent" onClick={onSaveSignature}>Save</SuspenseButton>
        </div>
    </div >
})
